import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Alert,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { verifyFreeTrialEmail } from "../../api/api";
import FMBlogo from "../../assests/logo.png";
import CompleteRegistrationForm from "./CompleteRegistrationForm";

const FreeTrialVerify = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [alreadyRegistered, setAlreadyRegistered] = useState(false);
    const [signupData, setSignupData] = useState(null);
    const [showRegistrationForm, setShowRegistrationForm] = useState(false);
    const [verifiedUser, setVerifiedUser] = useState(null);
    const navigate = useNavigate();
    const hasFetched = useRef(false);

    // Allow page to scroll (global css has overflow: hidden on html/body/#root)
    useEffect(() => {
        document.documentElement.classList.add("scrollable-page");
        document.body.classList.add("scrollable-page");
        const root = document.getElementById("root");
        if (root) root.classList.add("scrollable-page");
        return () => {
            document.documentElement.classList.remove("scrollable-page");
            document.body.classList.remove("scrollable-page");
            if (root) root.classList.remove("scrollable-page");
        };
    }, []);

    useEffect(() => {
        // Prevent duplicate API calls (idempotent)
        if (hasFetched.current) {
            return;
        }
        hasFetched.current = true;

        const verifyEmail = async () => {
            try {
                // Get parameters from Secure IVS callback
                const queryParams = new URLSearchParams(window.location.search);
                const status = queryParams.get("status");
                const userEmail = queryParams.get("user") || sessionStorage.getItem("freeTrialEmail");
                const callbackToken = queryParams.get("callback_token"); // From backend ivs-callback
                const returnCode = sessionStorage.getItem("ivs_return_code"); // From signup initiation

                // Check if verification was successful from Secure IVS
                if (status !== "true") {
                    setError("Email verification was not successful. Please try signing up again.");
                    setLoading(false);
                    return;
                }

                if (!userEmail) {
                    setError("Email not found. Please try signing up again.");
                    setLoading(false);
                    return;
                }

                if (!returnCode || !callbackToken) {
                    setError("Invalid verification. Please try signing up again from the signup page.");
                    setLoading(false);
                    return;
                }

                // Clear stored return code after use
                sessionStorage.removeItem("ivs_return_code");

                // Verify email with backend - pass both tokens for validation
                const response = await verifyFreeTrialEmail({
                    user: userEmail,
                    status: status,
                    return_code: returnCode,
                    callback_token: callbackToken,
                });

                console.log('📧 Free trial verification response:', {
                    success: response.success,
                    already_registered: response.already_registered,
                    is_new_signup: response.is_new_signup,
                    message: response.message,
                    signup_id: response.signup?.id
                });

                if (response.success) {
                    setSuccess(true);
                    setSignupData(response.signup);
                    
                    // Explicitly check if already_registered is true (boolean or string)
                    // Default to false if not explicitly true
                    const isAlreadyRegistered = 
                        response.already_registered === true || 
                        response.already_registered === 'true' ||
                        response.already_registered === 1;
                    
                    // Also check is_new_signup flag if available
                    const isNewSignup = response.is_new_signup === true || response.is_new_signup === 'true';
                    
                    if (isAlreadyRegistered && !isNewSignup) {
                        console.log('⚠️ Email already registered - showing login message');
                        setAlreadyRegistered(true);
                    } else {
                        console.log('✅ New signup - showing success message', {
                            already_registered: response.already_registered,
                            is_new_signup: response.is_new_signup
                        });
                        setAlreadyRegistered(false);
                        
                        // Auto-login: Store token and user data in localStorage
                        if (response.user) {
                            localStorage.setItem("token", response.token || response.user.id);
                            localStorage.setItem("user", JSON.stringify(response.user));
                            localStorage.setItem("branch_id", response.user.branch_id);
                            localStorage.setItem("client_id", response.user.client_id);
                            localStorage.setItem("branch_name", response.user.branch_name);
                            console.log('✅ User auto-logged in:', response.user);
                            // New signups: show Complete Registration form (skip profile edit, then go to service-management)
                            if (isNewSignup) {
                                setVerifiedUser(response.user);
                                setShowRegistrationForm(true);
                            }
                        }
                    }
                    
                    // Clear stored email
                    sessionStorage.removeItem("freeTrialEmail");
                } else {
                    // Check if this is an already registered error
                    const isAlreadyRegistered = 
                        response.already_registered === true || 
                        response.already_registered === 'true' ||
                        response.already_registered === 1;
                    
                    if (isAlreadyRegistered) {
                        setAlreadyRegistered(true);
                        setSuccess(true); // Show success state but with already registered message
                        // Don't store token for already-registered users - let them login properly
                    } else {
                        setError(response.message || "Verification failed. Please try again.");
                    }
                }
            } catch (err) {
                console.error("Error verifying email:", err);
                
                // Check if error response indicates already registered
                const errorData = err.response?.data;
                const isAlreadyRegistered = 
                    errorData?.already_registered === true || 
                    errorData?.already_registered === 'true' ||
                    errorData?.already_registered === 1;
                
                if (isAlreadyRegistered) {
                    setAlreadyRegistered(true);
                    setSuccess(true); // Show success state but with already registered message
                    setError(errorData?.message || "This email is already registered. Please login to use your account.");
                    // Don't store token for already-registered users - let them login properly
                } else {
                    setError(
                        errorData?.message ||
                        "An error occurred during verification. Please try again."
                    );
                }
            } finally {
                setLoading(false);
            }
        };

        verifyEmail();
    }, [navigate]);

    const handleContinue = () => {
        const token = localStorage.getItem("token");
        const branchId = localStorage.getItem("branch_id");
        if (token && branchId) {
            const isNewSignup = signupData && !alreadyRegistered;
            if (isNewSignup && !showRegistrationForm) {
                setShowRegistrationForm(true);
                setVerifiedUser(JSON.parse(localStorage.getItem("user") || "{}"));
            } else if (!isNewSignup) {
                window.location.href = "/home/profile";
            }
        } else {
            window.location.href = "/login";
        }
    };

    const handleRegistrationSuccess = async () => {
        localStorage.setItem("welcome_screen_shown", "true");
        // Use full page reload so App re-evaluates auth state from localStorage
        localStorage.setItem("startOnboardingTour", "true");
        window.location.href = "/home/panel";
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                minHeight: "100vh",
                px: { xs: 2, sm: 3 },
                py: { xs: 3, sm: 4 },
                backgroundColor: "#f5f5f5",
            }}
        >
            <Card
                sx={{
                    maxWidth: 500,
                    width: "100%",
                    boxShadow: 3,
                    borderRadius: 2,
                }}
            >
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            mb: 3,
                        }}
                    >
                        <img
                            src={FMBlogo}
                            alt="Logo"
                            style={{
                                width: 200,
                                maxWidth: "100%",
                                height: "auto",
                                marginBottom: 24,
                            }}
                        />
                        <Typography
                            variant="h4"
                            component="h1"
                            gutterBottom
                            sx={{
                                fontWeight: 600,
                                textAlign: "center",
                                fontSize: { xs: "1.75rem", sm: "2rem" },
                            }}
                        >
                            {loading 
                                ? "Verifying Email..." 
                                : alreadyRegistered 
                                    ? "Already Registered" 
                                    : success
                                        ? ""
                                        : "Verification Failed"}
                        </Typography>
                    </Box>

                    {loading && (
                        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                            <CircularProgress size={60} />
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                            {(alreadyRegistered || /already registered/i.test(error)) && (
                                <Box sx={{ mt: 1 }}>
                                    <Link
                                        to="/login"
                                        style={{ color: "#1976d2", fontWeight: 600, textDecoration: "underline" }}
                                    >
                                        Go to Login
                                    </Link>
                                </Box>
                            )}
                        </Alert>
                    )}

                    {success && signupData && showRegistrationForm && !alreadyRegistered && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
                                Complete the registration below to set up your easyQue account.
                            </Typography>
                            <CompleteRegistrationForm
                                initialData={verifiedUser}
                                onSuccess={handleRegistrationSuccess}
                            />
                        </Box>
                    )}

                    {success && signupData && !showRegistrationForm && (
                        <>
                            <Alert 
                                severity={alreadyRegistered ? "info" : "success"} 
                                sx={{ mb: 2 }}
                            >
                                {alreadyRegistered 
                                    ? "Already registered please login to use"
                                    : "Your email has been verified successfully!"}
                            </Alert>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body1" gutterBottom>
                                    <strong>Email:</strong> {signupData.email}
                                </Typography>
                                {signupData.name && (
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Name:</strong> {signupData.name}
                                    </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    {alreadyRegistered
                                        ? <>Your account is already set up. <a href="/login" style={{ color: "#1976d2", fontWeight: 600 }}>Go to Login</a> to access your account.</>
                                        : localStorage.getItem("token")
                                            ? "Complete the registration below to set up your easyQue account."
                                            : "Your free trial account is ready to use. Please login to access your account."}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                                onClick={handleContinue}
                                sx={{
                                    py: 1.5,
                                    fontSize: { xs: "0.9rem", sm: "1rem" },
                                    textTransform: "none",
                                    fontWeight: 600,
                                }}
                            >
                                {alreadyRegistered 
                                    ? "Go to Login" 
                                    : (localStorage.getItem("token") ? "Complete Registration" : "Continue")}
                            </Button>
                        </>
                    )}

                    {!loading && !success && (
                        <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            size="large"
                            onClick={() => navigate("/free-trial")}
                            sx={{
                                py: 1.5,
                                fontSize: { xs: "0.9rem", sm: "1rem" },
                                textTransform: "none",
                                fontWeight: 600,
                            }}
                        >
                            Try Again
                        </Button>
                    )}
                </CardContent>
            </Card>

            <Box mt={4} textAlign="center">
                <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                    <a href="#" style={{ textDecoration: "none", color: "inherit" }}>
                        easyQue
                    </a>{" "}
                    (c) by Digifront
                </Typography>
                <Typography variant="body2" mt={1} sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                    <a
                        href="mailto:sales@digifront.biz"
                        style={{ textDecoration: "none", color: "inherit" }}
                    >
                        sales@digifront.biz
                    </a>
                </Typography>
            </Box>
        </Box>
    );
};

export default FreeTrialVerify;
